/**
 * Los colores propuestos desde el logotipo (docs/alta-de-web-con-plantillas.md §8,
 * paso «Marca»; fase F). Se calculan en el navegador, sin llamar a nadie: el logo se
 * pinta pequeño en un lienzo, se cuentan sus colores por cubos y salen tres —el
 * dominante con color para el primario, el más distinto en tono para el acento y uno
 * oscuro para el secundario—. Son una **propuesta**: el cliente los acepta con un botón
 * o los cambia.
 */
export type Paleta = { primario: string; secundario: string; acento: string }

type Color = { n: number; r: number; g: number; b: number; h: number; s: number; l: number }

const hex = (r: number, g: number, b: number) => `#${[r, g, b].map((v) => Math.max(0, Math.min(255, Math.round(v))).toString(16).padStart(2, '0')).join('')}`

const hsl = (r: number, g: number, b: number) => {
  const R = r / 255
  const G = g / 255
  const B = b / 255
  const max = Math.max(R, G, B)
  const min = Math.min(R, G, B)
  const l = (max + min) / 2
  const d = max - min
  if (d === 0) return { h: 0, s: 0, l }
  const s = d / (1 - Math.abs(2 * l - 1))
  let h = max === R ? ((G - B) / d) % 6 : max === G ? (B - R) / d + 2 : (R - G) / d + 4
  h = Math.round(h * 60)
  if (h < 0) h += 360
  return { h, s, l }
}

const rgb = (h: number, s: number, l: number) => {
  const c = (1 - Math.abs(2 * l - 1)) * s
  const x = c * (1 - Math.abs(((h / 60) % 2) - 1))
  const m = l - c / 2
  const [r, g, b] = h < 60 ? [c, x, 0] : h < 120 ? [x, c, 0] : h < 180 ? [0, c, x] : h < 240 ? [0, x, c] : h < 300 ? [x, 0, c] : [c, 0, x]
  return hex((r! + m) * 255, (g! + m) * 255, (b! + m) * 255)
}

/**
 * Un color sacado de otro: girado de tono y/o con otra luz, para cuando el logo no da
 * un segundo color. **De un logo sin color no sale color**: girarle el tono a un gris
 * —que no tiene tono— proponía un granate y un turquesa que no están en el logo, y el
 * cliente veía una marca que no es la suya. Un logo monocromo da grises.
 */
const derivado = (de: Color, giro: number, luz?: number) =>
  de.s < 0.1 ? rgb(0, 0, Math.max(0, Math.min(1, luz ?? de.l))) : rgb((de.h + giro) % 360, Math.max(0.25, Math.min(0.85, de.s)), luz ?? de.l)

const distanciaDeTono = (a: Color, b: Color) => {
  const d = Math.abs(a.h - b.h)
  return Math.min(d, 360 - d)
}

/**
 * De los píxeles (RGBA, como los da un lienzo) de un logo, la paleta. `null` si no
 * hay nada que mirar (todo transparente o blanco). Puro, para poder probarlo.
 */
export const paletaDe = (pixeles: ArrayLike<number>): Paleta | null => {
  const cubos = new Map<number, { n: number; r: number; g: number; b: number }>()
  for (let i = 0; i + 3 < pixeles.length; i += 4) {
    if (pixeles[i + 3]! < 128) continue
    const r = pixeles[i]!
    const g = pixeles[i + 1]!
    const b = pixeles[i + 2]!
    // El fondo blanco (o casi) no es un color del logo.
    if (r > 235 && g > 235 && b > 235) continue
    const clave = ((r >> 4) << 8) | ((g >> 4) << 4) | (b >> 4)
    const cubo = cubos.get(clave) ?? { n: 0, r: 0, g: 0, b: 0 }
    cubo.n++
    cubo.r += r
    cubo.g += g
    cubo.b += b
    cubos.set(clave, cubo)
  }
  const colores: Color[] = [...cubos.values()].map((c) => {
    const r = c.r / c.n
    const g = c.g / c.n
    const b = c.b / c.n
    return { n: c.n, r, g, b, ...hsl(r, g, b) }
  })
  if (colores.length === 0) return null
  const total = colores.reduce((t, c) => t + c.n, 0)
  const peso = (c: Color) => c.n * (0.5 + c.s)
  const porPeso = (lista: Color[]) => [...lista].sort((a, b) => peso(b) - peso(a))
  const conColor = colores.filter((c) => c.s > 0.25 && c.l > 0.12 && c.l < 0.9)

  // El primario: el que más pesa entre los que tienen color; un logo en negro da su gris.
  const primario = porPeso(conColor)[0] ?? porPeso(colores)[0]!
  // El acento: otro color de verdad —vivo, no oscuro—, lejos en tono y con presencia; si no lo hay, el complementario.
  const acento = porPeso(conColor.filter((c) => c !== primario && c.l >= 0.3 && distanciaDeTono(c, primario) >= 40 && c.n / total >= 0.03))[0]
  // El secundario: algo oscuro con presencia; si no, el propio primario oscurecido.
  const secundario = colores.filter((c) => c !== primario && c !== acento && c.l < 0.35 && c.n / total >= 0.03).sort((a, b) => b.n - a.n)[0]

  return {
    primario: hex(primario.r, primario.g, primario.b),
    secundario: secundario ? hex(secundario.r, secundario.g, secundario.b) : derivado(primario, 0, 0.18),
    acento: acento ? hex(acento.r, acento.g, acento.b) : derivado(primario, 180, 0.55),
  }
}

/** La paleta de un fichero de imagen, en el navegador. `null` si no se puede leer o no da color. */
export const coloresDelLogo = async (fichero: File): Promise<Paleta | null> => {
  const lado = 64
  try {
    // Se decodifica ya al tamaño que se va a mirar: un PNG de 10000×10000 son cientos de
    // megas en la pestaña, y aquí solo se cuentan colores en 64×64.
    const mapa = await createImageBitmap(fichero, { resizeWidth: lado, resizeHeight: lado, resizeQuality: 'low' })
    try {
      const lienzo = document.createElement('canvas')
      lienzo.width = lado
      lienzo.height = lado
      const ctx = lienzo.getContext('2d')
      if (!ctx) return null
      ctx.drawImage(mapa, 0, 0, lado, lado)
      return paletaDe(ctx.getImageData(0, 0, lado, lado).data)
    } finally {
      mapa.close()
    }
  } catch {
    return null
  }
}
